/**
 * Whether the user is controlling the camera, from the press that starts a
 * pointer gesture until the camera has stopped after its release.
 *
 * Camera motion alone cannot tell: a slow orbit pauses between pointer events
 * for longer than any rest window, so motion-based quality would flip on every
 * pause. The renderer therefore keeps its motion budget for the whole gesture.
 *
 * - `controlstart` holds the flag; in `releasing` it goes back to `held`
 *   without the flag ever dropping in between.
 * - `controlend` enters `releasing`; the flag stays set.
 * - In `releasing`, a frame in which the controls dispatched no `update`
 *   (the camera did not move) returns to `idle`.
 *
 * Release is decided per frame and never from the library's `rest`/`sleep`
 * events or its `active` flag: a gesture that never moved the camera leaves
 * those stale, and the flag would stick. Wheel input dispatches neither
 * `controlstart` nor `controlend` and never holds the flag.
 */
export type UserCameraControlPhase = "idle" | "held" | "releasing";

export interface UserCameraControlState {
  readonly phase: UserCameraControlPhase;
  /** An `update` arrived since the last frame. Only read in `releasing`. */
  readonly moved: boolean;
}

export type UserCameraControlEvent =
  | "controlstart"
  | "controlend"
  | "update"
  | "frame";

export const IDLE_USER_CAMERA_CONTROL: UserCameraControlState = {
  phase: "idle",
  moved: false,
};

export function reduceUserCameraControl(
  state: UserCameraControlState,
  event: UserCameraControlEvent,
): UserCameraControlState {
  switch (event) {
    case "controlstart":
      return { phase: "held", moved: false };
    case "controlend":
      return state.phase === "held"
        ? { phase: "releasing", moved: false }
        : state;
    case "update":
      return state.phase === "releasing" && !state.moved
        ? { phase: "releasing", moved: true }
        : state;
    case "frame":
      if (state.phase !== "releasing") return state;
      return state.moved
        ? { phase: "releasing", moved: false }
        : IDLE_USER_CAMERA_CONTROL;
  }
}

export const isUserControllingCamera = (state: UserCameraControlState) =>
  state.phase !== "idle";

/** The part of camera-controls the tracker listens to. */
export interface UserCameraControlSource {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
  cancel(): void;
}

interface UserCameraControlTrackerOptions {
  /** Receives every change of the flag, and only changes. */
  onChange: (controlling: boolean) => void;
  /** Requests a frame; the demand loop runs no frame of its own. */
  invalidate: () => void;
  /** Window to watch for a lost focus; omitted outside a browser. */
  window?: Pick<Window, "addEventListener" | "removeEventListener">;
}

/**
 * Wires {@link reduceUserCameraControl} to a camera-controls instance. Call
 * `frame()` once per rendered frame after the controls updated (drei updates
 * them at priority -1). `detach()` clears a set flag.
 */
export function trackUserCameraControl(
  controls: UserCameraControlSource,
  { onChange, invalidate, window }: UserCameraControlTrackerOptions,
) {
  let state = IDLE_USER_CAMERA_CONTROL;

  const dispatch = (event: UserCameraControlEvent) => {
    const previous = state;
    state = reduceUserCameraControl(state, event);
    const was = isUserControllingCamera(previous);
    const is = isUserControllingCamera(state);
    if (was !== is) {
      onChange(is);
      invalidate();
    } else if (
      state.phase === "releasing" &&
      (previous.phase === "held" || (event === "frame" && previous.moved))
    ) {
      // Releasing needs one frame without an `update` to end. A release on a
      // still camera, or an `update` dispatched outside a frame (a synchronous
      // `controls.update(0)`), would otherwise leave no frame to notice it.
      invalidate();
    }
  };

  const onControlStart = () => dispatch("controlstart");
  const onControlEnd = () => dispatch("controlend");
  const onUpdate = () => dispatch("update");
  // A pointerup outside a focused window never arrives; cancel() ends the
  // gesture through the library's own controlend.
  const onBlur = () => controls.cancel();

  controls.addEventListener("controlstart", onControlStart);
  controls.addEventListener("controlend", onControlEnd);
  controls.addEventListener("update", onUpdate);
  window?.addEventListener("blur", onBlur);

  return {
    frame: () => dispatch("frame"),
    detach: () => {
      controls.removeEventListener("controlstart", onControlStart);
      controls.removeEventListener("controlend", onControlEnd);
      controls.removeEventListener("update", onUpdate);
      window?.removeEventListener("blur", onBlur);
      if (isUserControllingCamera(state)) onChange(false);
      state = IDLE_USER_CAMERA_CONTROL;
    },
  };
}
